import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Toasts globales — §"la mayoría de respuestas del back no se muestran".
 *
 * Antes, mostrar el error de una mutation dependía de que cada página
 * recordara renderizar `<Aviso>{mutation.error.message}</Aviso>`. Una
 * auditoría encontró ~24 mutations (de 52) sin ese render: el usuario le daba
 * a un botón, el backend respondía con un motivo claro (permiso denegado,
 * regla de negocio, validación), y ese motivo se perdía en silencio.
 *
 * En vez de parchear cada página, `main.tsx` conecta el `onError` global del
 * `QueryClient` a `mostrarError` de este módulo: cualquier mutation que falle
 * en cualquier parte de la app dispara un toast, exista o no un `<Aviso>`
 * local. Una página que ya maneja su error con más detalle simplemente
 * termina mostrando ambos — el toast desaparece solo, el aviso local queda.
 */

interface Toast {
  id: number;
  tono: 'error' | 'exito';
  texto: string;
}

interface ContextoToasts {
  mostrarError: (texto: string) => void;
  mostrarExito: (texto: string) => void;
}

const ContextoToastsInterno = createContext<ContextoToasts | null>(null);

const DURACION_MS = 6000;

export function ProveedorToasts({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const siguienteId = useRef(1);

  const quitar = useCallback((id: number) => {
    setToasts((previos) => previos.filter((t) => t.id !== id));
  }, []);

  const agregar = useCallback(
    (tono: Toast['tono'], texto: string) => {
      const id = siguienteId.current++;
      setToasts((previos) => [...previos, { id, tono, texto }]);
      setTimeout(() => quitar(id), DURACION_MS);
    },
    [quitar],
  );

  const valor = useMemo<ContextoToasts>(
    () => ({
      mostrarError: (texto) => agregar('error', texto),
      mostrarExito: (texto) => agregar('exito', texto),
    }),
    [agregar],
  );

  useEffect(() => {
    registrarManejadorGlobalDeErrores(valor.mostrarError);
    registrarManejadorGlobalDeExito(valor.mostrarExito);
    return () => {
      registrarManejadorGlobalDeErrores(() => undefined);
      registrarManejadorGlobalDeExito(() => undefined);
    };
  }, [valor]);

  return (
    <ContextoToastsInterno.Provider value={valor}>
      {children}
      <div className="toasts-host" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tono}`}>
            <span>{t.texto}</span>
            <button type="button" aria-label="Cerrar" onClick={() => quitar(t.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </ContextoToastsInterno.Provider>
  );
}

export function useToasts(): ContextoToasts {
  const contexto = useContext(ContextoToastsInterno);
  if (!contexto) throw new Error('useToasts debe usarse dentro de <ProveedorToasts>');
  return contexto;
}

/**
 * Referencia mutable fuera de React: el `QueryClient` se crea antes de que
 * exista cualquier componente (en `main.tsx`, junto a `createRoot`), así que
 * su `onError` global no puede llamar a `useToasts()` directamente. Se llena
 * una vez montado `<ProveedorToasts>` y con eso alcanza para el resto de la
 * vida de la página.
 */
let mostrarErrorGlobal: ((texto: string) => void) | null = null;

export function registrarManejadorGlobalDeErrores(fn: (texto: string) => void): void {
  mostrarErrorGlobal = fn;
}

export function notificarErrorGlobal(texto: string): void {
  mostrarErrorGlobal?.(texto);
}

/**
 * Misma mecánica que el error global, para el mensaje de confirmación que
 * llega en el header `X-Mensaje-Exito` (ver `servicios/api.ts`): el cliente
 * de la API se crea antes que cualquier componente, así que no puede llamar
 * a `useToasts()` directamente.
 */
let mostrarExitoGlobal: ((texto: string) => void) | null = null;

export function registrarManejadorGlobalDeExito(fn: (texto: string) => void): void {
  mostrarExitoGlobal = fn;
}

export function notificarExitoGlobal(texto: string): void {
  mostrarExitoGlobal?.(texto);
}
